import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _forEachRight from "lodash/forEachRight";
import _isEmpty from "lodash/isEmpty";
import _sortBy from "lodash/sortBy";
import _each from "lodash/each";
import _includes from "lodash/includes";
import "core-js/modules/es6.array.map.js";
import "core-js/modules/es6.regexp.split.js";
import moment from 'moment';
import { formatMoney } from "./string-utils";
import { DATE_FORMAT } from "../constants";
export var CREDIT_SCORE_MIN = 300;
export var CREDIT_SCORE_MAX = 850; // poor, average, good, excellent

var statusColors = ['red', // poor
'orange', // average
'yellow', // good
'green' // excellent
];
export var shortHandMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export var formatTenDigitPhone = function formatTenDigitPhone(str) {
  return "(".concat(str.substring(0, 3), ") ").concat(str.substring(3, 6), "-").concat(str.substring(6, 10));
};
export var formatAccountName = function formatAccountName(str) {
  var splitOn = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ' ';
  if (!str) return str;
  return str.split(splitOn).map(function (i) {
    // If there is a . (Nerdwallet N.B) let's keep each letter capitalized
    if (i.indexOf('.') > -1) return formatAccountName(i, '.'); // If there is a / (Nerdwallet/Chase) let's keep each letter capitalized

    if (i.indexOf('/') > -1) return formatAccountName(i, '/'); // If there is & (Ny&Co) let's keep each letter capitalized

    if (i.indexOf('&') > -1) return formatAccountName(i, '&'); // If the whole thing isn't uppercase, let's keep that formatting

    if (i.length > 1 && i.toUpperCase() !== i) return i; // title case as a fallback

    return i && i[0].toUpperCase() + i.substring(1).toLowerCase();
  }).join(splitOn);
};

var stringifyYearMonth = function stringifyYearMonth(years, months) {
  var yearPlural = ' yrs';

  if (years === 1) {
    yearPlural = ' yr';
  }

  var monthPlural = ' mos';

  if (months === 1) {
    monthPlural = ' mo';
  }

  return "".concat(years + yearPlural, " ").concat(months).concat(monthPlural);
}; // percentage variable is rounded the same way the value displayed to the user is rounded
// we do this to prevent inconsistencies with the status bucket and value displayed to user


export var getCreditUtilizationStatus = function getCreditUtilizationStatus(percentage) {
  if (percentage >= 50) return ['Poor', 0];
  if (percentage >= 30) return ['Average', 1];
  if (percentage >= 10) return ['Good', 2];
  return ['Excellent', 3];
};
export var getCreditScoreStatus = function getCreditScoreStatus(score) {
  if (score >= 720) return 'Excellent';
  if (score >= 690) return 'Good';
  if (score >= 630) return 'Average';
  return 'Poor';
};

var getCreditUtilData = function getCreditUtilData(data) {
  var creditUtilData = {};
  var creditUtil = Math.round(data.percent_utilization * 100);
  creditUtilData.credit_util = "".concat(creditUtil, "%");
  var status = getCreditUtilizationStatus(creditUtil);

  var _status = _slicedToArray(status, 2),
      firstStatus = _status[0],
      secondStatus = _status[1];

  creditUtilData.credit_util_status = firstStatus;
  creditUtilData.selected = secondStatus;
  creditUtilData.total_usage = 0;
  creditUtilData.total_limits = 0;
  creditUtilData.accounts = [];
  return creditUtilData;
};

var addCreditUtilAccounts = function addCreditUtilAccounts(account, parsed, id) {
  var creditUtilData = parsed.creditUtilData;

  if (account.account_subtype && account.account_subtype.indexOf('Revolving') > -1 || account.account_type && account.account_type.indexOf('Revolving') > -1) {
    creditUtilData.total_usage += account.current_balance;
    creditUtilData.total_limits += account.credit_limit;
    var accountStatus = "Balance ".concat(formatMoney(account.current_balance));
    var creditLimit = account.credit_limit;

    if (creditLimit === 0) {
      if (account.high_balance) {
        creditLimit = account.high_balance;
      }
    }

    var usagePercentage = 100;

    if (creditLimit > 0) {
      usagePercentage = account.current_balance / creditLimit * 100;
    }

    var utilStatus = getCreditUtilizationStatus(usagePercentage);

    if (usagePercentage > 100) {
      usagePercentage = '100+';
    } else {
      usagePercentage = Math.round(usagePercentage);
    }

    creditUtilData.accounts.push({
      id: id,
      name: account.creditor_name,
      percentage: "".concat(usagePercentage, "%"),
      status: accountStatus,
      limit: formatMoney(creditLimit),
      color: statusColors[utilStatus[1]]
    });
  }

  return creditUtilData;
};

export var getPaymentHistoryStatus = function getPaymentHistoryStatus(percentage) {
  if (percentage === 100) return ['Excellent', 3];
  if (percentage >= 99) return ['Good', 2];
  if (percentage >= 97) return ['Average', 1];
  return ['Poor', 0];
};

var getPaymentHistoryData = function getPaymentHistoryData() {
  var paymentHistoryData = {}; // initialize data to be added later when parsing accounts

  paymentHistoryData.total_payments = 0;
  paymentHistoryData.on_time_payments = 0;
  paymentHistoryData.percent_on_time = '0%';
  paymentHistoryData.accounts = [];
  return paymentHistoryData;
};

var addPaymentHistoryAccounts = function addPaymentHistoryAccounts(account, parsed, id) {
  var payHistoryData = parsed.payHistoryData;
  var payHistory = account.pay_history_status;
  var totalPayments = 0;
  var totalLength = 0;
  var onTimePayments = 0;

  if (payHistory) {
    totalPayments = payHistory.length;
    totalLength = payHistory.length;
  }

  for (var i = 0; i < totalLength; i += 1) {
    if (payHistory.charAt(i) === 'C') {
      onTimePayments += 1;
    } else if (payHistory.charAt(i) === 'U') {
      totalPayments -= 1;
    }
  }

  var paymentPercentage = 0;

  if (totalPayments < onTimePayments || totalPayments === 0) {
    paymentPercentage = 100;
  } else if (totalPayments > 0) {
    paymentPercentage = onTimePayments / totalPayments * 100;
  }

  var paymentStatus = getPaymentHistoryStatus(paymentPercentage);
  payHistoryData.total_payments += totalPayments;
  payHistoryData.on_time_payments += onTimePayments;
  payHistoryData.accounts.push({
    id: id,
    name: account.creditor_name,
    percentage: "".concat(Math.round(paymentPercentage), "%"),
    status: "On time: ".concat(onTimePayments),
    limit: "Total payments: ".concat(totalPayments),
    color: statusColors[paymentStatus[1]]
  });
  return payHistoryData;
};

export var getDerogatoryStatus = function getDerogatoryStatus(total) {
  if (total === 0) return ['Excellent', 3];
  if (total <= 1) return ['Good', 2];
  if (total <= 3) return ['Average', 1];
  return ['Poor', 0];
};

var getDerogatoryData = function getDerogatoryData(data) {
  var derogatoryData = {};
  var totalDerogatory = data.num_derogatory_accounts;
  derogatoryData.total_derogatory = totalDerogatory;
  derogatoryData.accounts = [];
  var status = getDerogatoryStatus(totalDerogatory);

  var _status2 = _slicedToArray(status, 2),
      firstStatus = _status2[0],
      secondStatus = _status2[1];

  derogatoryData.derogatory_status = firstStatus;
  derogatoryData.selected = secondStatus;
  return derogatoryData;
}; // TODO: Either expose and then consume TU symbol, or move this business logic to the BE


var addDerogatoryAccounts = function addDerogatoryAccounts(account, parsed, id) {
  // F: derogatory account condition
  // https://github.com/NerdWallet/identity.nerdwallet/blob/master/src/identity/lib/tu_utils/actions.py#L1095
  // We had to make some educated guess regarding the late payment abbreviations as well as the foreclosure
  // and `repo/foreclosure` abbreviations. There wasn't any documentation on what the abbreviations should be
  // and there were no known examples of users having those marks in DWH.
  if (_includes(['Derogatory', 'Derog', 'F'], account.account_condition) || _includes(['Delinq 150', 'Delinq 180', 'WEP', 'Repo/Foreclosure', 'Foreclosure', 'Repo', 'Coll/Chargeoff'], account.pay_status)) {
    parsed.derogatoryData.accounts.push({
      id: id,
      name: account.creditor_name,
      status: '',
      color: '@darkergrey'
    });
  }
};

export var getAverageAgeStatus = function getAverageAgeStatus(age) {
  if (age >= 9) return ['Excellent', 3];
  if (age >= 7) return ['Good', 2];
  if (age >= 5) return ['Average', 1];
  return ['Poor', 0];
};

var getAverageAgeData = function getAverageAgeData(data) {
  var averageAgeData = {}; // use average age of accounts instead of age of credit history for now...
  // const totalOpenAccounts = data.num_accounts - data.num_closed_accounts
  // const avgAge = Math.round(data.sum_age_of_credit / (totalOpenAccounts > 0 ? totalOpenAccounts : 1))
  // will compute final average after accounts have been parsed

  averageAgeData.average_age = 0;
  averageAgeData.total_months = 0;
  averageAgeData.total_years = 0;
  averageAgeData.total_accounts = data.num_open_accounts;
  averageAgeData.accounts = [];
  return averageAgeData;
};

var addAverageAgeAccounts = function addAverageAgeAccounts(account, parsed, id) {
  var averageAgeData = parsed.averageAgeData;
  var accountStarted = moment(account.date_opened_at);
  var accountYears = Math.floor(account.months_reviewed / 12);
  var accountMonths = account.months_reviewed % 12;

  if (accountStarted) {
    var today = moment();
    var monthDiff = today.diff(accountStarted, 'months');
    accountYears = Math.floor(monthDiff / 12);
    accountMonths = monthDiff % 12;
  }

  var ageStatus = getAverageAgeStatus(accountYears);
  averageAgeData.total_months += accountMonths;
  averageAgeData.total_years += accountYears;
  averageAgeData.accounts.push({
    id: id,
    name: account.creditor_name,
    status: stringifyYearMonth(accountYears, accountMonths),
    color: statusColors[ageStatus[1]]
  });
  return averageAgeData;
};

export var getTotalAccountsStatus = function getTotalAccountsStatus(total) {
  if (total >= 21) return ['Excellent', 3];
  if (total >= 11) return ['Good', 2];
  if (total >= 6) return ['Average', 1];
  return ['Poor', 0];
};

var getTotalAccountsData = function getTotalAccountsData(data) {
  var totalAccountsData = {};
  var totalAccounts = data.num_accounts;
  totalAccountsData.total_accounts = totalAccounts;
  var status = getTotalAccountsStatus(totalAccounts);

  var _status3 = _slicedToArray(status, 2),
      firstStatus = _status3[0],
      secondStatus = _status3[1];

  totalAccountsData.total_accounts_status = firstStatus;
  totalAccountsData.selected = secondStatus;
  totalAccountsData.accounts = [];
  return totalAccountsData;
};

var addTotalAccounts = function addTotalAccounts(account, parsed, id) {
  var accountIsOpen = account.open_closed === 'Open' || account.account_condition === 'Open';
  var totalAccountStatus;

  if (accountIsOpen) {
    var currentBalance = account.current_balance;

    if (!currentBalance) {
      currentBalance = 0;
    }

    totalAccountStatus = "Balance ".concat(formatMoney(currentBalance));
  } else {
    totalAccountStatus = 'Closed';
  }

  parsed.totalAccountsData.accounts.push({
    id: id,
    name: account.creditor_name,
    status: totalAccountStatus,
    color: '@charcoal'
  });
};

export var getInquiriesStatus = function getInquiriesStatus(total) {
  if (total === 0) return ['Excellent', 3];
  if (total <= 2) return ['Good', 2];
  if (total <= 5) return ['Average', 1];
  return ['Poor', 0];
};

var getInquiriesData = function getInquiriesData(data) {
  var inquiriesData = {};
  var today = new Date();
  var twoYears = new Date(today.getFullYear() - 2, today.getMonth(), today.getDate());
  var inquiries = data.inquiries;
  var totalInquiries = 0;
  inquiriesData.accounts = [];

  if (!(inquiries == null || inquiries.length === 0)) {
    _each(inquiries, function (inquiry, i) {
      var date = new Date(inquiry.inquired_at);

      if (date >= twoYears && date <= today) {
        inquiriesData.accounts.push({
          id: i,
          name: inquiry.subscriber_name,
          status: moment(date).format(DATE_FORMAT.AMERICAN),
          color: '#929292'
        });
        totalInquiries += 1;
      }
    });
  }

  inquiriesData.total_inquiries = totalInquiries;
  var status = getInquiriesStatus(totalInquiries);

  var _status4 = _slicedToArray(status, 2),
      firstStatus = _status4[0],
      secondStatus = _status4[1];

  inquiriesData.inquiries_status = firstStatus;
  inquiriesData.selected = secondStatus;
  return inquiriesData;
};

var addAccountData = function addAccountData(data, parsed) {
  if (!data) return;
  var creditUtilData = parsed.creditUtilData,
      averageAgeData = parsed.averageAgeData,
      payHistoryData = parsed.payHistoryData;
  var oldestCard = '';
  var oldestAge = moment();

  _each(data, function (account, i) {
    if (account.open_closed === 'Open' || account.account_condition === 'Open') {
      creditUtilData = addCreditUtilAccounts(account, parsed, i);
      payHistoryData = addPaymentHistoryAccounts(account, parsed, i);
      averageAgeData = addAverageAgeAccounts(account, parsed, i);

      if (account.account_subtype && account.account_subtype.indexOf('Revolving') > -1 || account.account_type && account.account_type.indexOf('Revolving') > -1) {
        var openDate = moment(account.date_opened_at);

        if (openDate < oldestAge) {
          oldestAge = openDate;
          oldestCard = account.creditor_name;
        }
      }
    }

    addTotalAccounts(account, parsed, i);
    addDerogatoryAccounts(account, parsed, i);
  }); // total accounts includes both open and closed accounts
  // TU data sometimes alternates between the two types, making it difficult to read
  // sort by 'status' so open accounts are together first, followed by closed accounts
  // eslint-disable-next-line no-param-reassign


  parsed.totalAccountsData.accounts = _sortBy(parsed.totalAccountsData.accounts, 'status'); // add info snippets for simulator

  var simulatorData = {};
  simulatorData.oldestCard = oldestCard;
  simulatorData.creditBalances = parsed.creditUtilData.total_usage;
  simulatorData.totalCredit = parsed.creditUtilData.total_limits; // format finalized data for credit utilization

  creditUtilData.total_usage = formatMoney(parsed.creditUtilData.total_usage);
  creditUtilData.total_limits = formatMoney(parsed.creditUtilData.total_limits); // calculate average account age

  var totalYears = parsed.averageAgeData.total_years;
  var totalAccounts = parsed.averageAgeData.total_accounts;
  var totalMonths = Math.round((parsed.averageAgeData.total_months + 12 * totalYears) / totalAccounts);
  var finalYears = Math.floor(totalMonths / 12);
  var finalMonths = totalMonths % 12;
  var averageAgeStatus = getAverageAgeStatus(finalYears);
  averageAgeData.average_age = stringifyYearMonth(finalYears, finalMonths);

  var _averageAgeStatus = _slicedToArray(averageAgeStatus, 2),
      firstAgeStatus = _averageAgeStatus[0],
      secondAgeStatus = _averageAgeStatus[1];

  averageAgeData.age_status = firstAgeStatus;
  averageAgeData.selected = secondAgeStatus; // calculate total payment history percentage

  var percentage = 100;

  if (parsed.payHistoryData.total_payments > 0) {
    percentage = Math.round(parsed.payHistoryData.on_time_payments / parsed.payHistoryData.total_payments * 100);
  }

  var paymentHistoryStatus = getPaymentHistoryStatus(percentage);
  payHistoryData.percent_on_time = "".concat(percentage, "%");

  var _paymentHistoryStatus = _slicedToArray(paymentHistoryStatus, 2),
      firstPaymentStatus = _paymentHistoryStatus[0],
      secondPaymentStatus = _paymentHistoryStatus[1];

  payHistoryData.payment_status = firstPaymentStatus;
  payHistoryData.selected = secondPaymentStatus;
  var newData = {};
  newData.simulatorData = simulatorData;
  newData.payHistoryData = payHistoryData;
  newData.averageAgeData = averageAgeData;
  newData.creditUtilData = creditUtilData;
  return newData;
};

export var parseTUData = function parseTUData(data) {
  var parsed = {}; // data is pre-formatted to make sharing the different report components simpler
  // values like those sent to simulator are used as numerical limits and aren't pre-formatted

  parsed.creditUtilData = getCreditUtilData(data);
  parsed.payHistoryData = getPaymentHistoryData(data);
  parsed.derogatoryData = getDerogatoryData(data);
  parsed.averageAgeData = getAverageAgeData(data);
  parsed.totalAccountsData = getTotalAccountsData(data);
  parsed.inquiriesData = getInquiriesData(data);
  var accountData = addAccountData(data.tradelines, parsed);
  parsed = _objectSpread(_objectSpread({}, parsed), accountData);
  return parsed;
};
/**
 * Returns the signed change in credit score over the given duration, or otherwise
 * the first entry satisfying the given duration. If no entries satisfy the duration,
 * then the oldest available entry will be used.
 *
 * @param {Object} history     Credit score history, in ascending date order
 * @param {number} creditScore Current credit score
 * @param {Object} duration    Timespan over which to calculate delta, e.g. {days: 7, months: 1}
 *
 * @return {number|null} Difference in credit score over the given duration
 */

export var getCreditScoreDelta = function getCreditScoreDelta(history, creditScore, duration) {
  if (_isEmpty(history) || !creditScore) {
    return null;
  }

  var today = moment();
  var delta = creditScore - history[0].value; // use the oldest entry by default

  _forEachRight(history, function (entry) {
    if (moment(entry.pulled_at).add(duration).isSameOrBefore(today)) {
      delta = creditScore - entry.value;
      return false; // break the forEach
    }
  });

  return delta;
};